import { ReactNode } from 'react';
import logProPurchaseGetStartedClicked from '@alltrails/analytics/events/logProPurchaseGetStartedClicked';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { UserId } from '@alltrails/shared/types/User';
import Plan from 'types/Plan';
import Coupon from 'types/Coupon';
import useExperiment from 'hooks/useExperiment';
import * as styles from 'components/LandingPages/PrimaryLink/PrimaryLink.module.scss';
import CheckoutModal from 'components/CheckoutModal';
import Button, { ButtonProps } from '@alltrails/shared/denali/components/Button';
import SignupModal from 'components/SignupModal';
import { wrapUrlSafe } from 'utils/language_support_util';
import useToggle from '@alltrails/shared/hooks/useToggle';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';

export default function PrimaryLink({
  buttonText,
  coupon,
  plan,
  to,
  userId,
  desktopVariant,
  mobileVariant
}: {
  buttonText: ReactNode;
  plan: Plan;
  coupon: Coupon;
  to: string;
  userId: UserId;
  desktopVariant: ButtonProps['variant'];
  mobileVariant: ButtonProps['variant'];
}) {
  const languageRegionCode = useLanguageRegionCode();
  const experiment = useExperiment('growth-v2-on-page-web-checkout');
  const [isCheckoutModalOpen, toggleCheckoutModal] = useToggle();
  const [isSignupModalOpen, toggleSignupModal] = useToggle();

  return (
    <div className={styles.buttonContainer}>
      <CheckoutModal
        coupon={coupon}
        isOpen={isCheckoutModalOpen}
        onRequestClose={toggleCheckoutModal}
        plan={plan}
        trigger={CarouselDisplayTrigger.DirectNav}
        userId={userId}
      />
      <SignupModal
        heading={<FormattedMessage defaultMessage="Do more outdoors{br}with AllTrails+" values={{ br: <br /> }} />}
        trigger={CarouselDisplayTrigger.DirectNav}
        isOpen={isSignupModalOpen}
        onRequestClose={() => {
          toggleSignupModal();
        }}
        onSuccess={() => {
          toggleSignupModal();
          toggleCheckoutModal();
        }}
      />
      {/*
        Two buttons are needed in order to show different button variants based on screen width.
        Setting the variant prop to the 'useIsMobile' value results in flickering on page load.
      */}
      <Button
        className={styles.desktopButton}
        size="lg"
        width="fullOnMobile"
        text={buttonText}
        {...(experiment?.value !== 'treatment'
          ? {
              linkInfo: {
                href: wrapUrlSafe(to, languageRegionCode),
                useReactRouter: true
              }
            }
          : {})}
        onClick={() => {
          logProPurchaseGetStartedClicked({
            purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
          });

          if (experiment?.value === 'treatment') {
            if (!userId) {
              toggleSignupModal();
            } else {
              toggleCheckoutModal();
            }
          }
        }}
        testId="simple-pro-landing_page-primary"
        variant={desktopVariant}
      />
      <Button
        className={styles.mobileButton}
        size="lg"
        width="fullOnMobile"
        text={buttonText}
        {...(experiment?.value !== 'treatment'
          ? {
              linkInfo: {
                href: wrapUrlSafe(to, languageRegionCode),
                useReactRouter: true
              }
            }
          : {})}
        onClick={() => {
          logProPurchaseGetStartedClicked({
            purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
          });

          if (experiment?.value === 'treatment') {
            if (!userId) {
              toggleSignupModal();
            } else {
              toggleCheckoutModal();
            }
          }
        }}
        testId="simple-pro-landing_page-primary"
        variant={mobileVariant}
        allowWrap
      />
    </div>
  );
}
